import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { IElementsById } from "core-integration/src/store/elements/types";
import { IPickLight } from "core-integration/src/store/entries/types";
import { getRules } from "core-integration/src/store/game/reducers";
import { IRules } from "core-integration/src/store/game/types";
import { size } from "polished";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components/";
import Captain from "../../img/icons/captain.svg?react";
import Sub from "../../img/icons/sub.svg?react";
import ViceCaptain from "../../img/icons/vice-captain.svg?react";
import x2 from "../../img/icons/x2.svg?react";
import ElementDialogButton from "../ElementDialogButton";
import {
  Controls,
  InfoControl,
  PitchElementWrap,
  PrimaryControl,
  StyledPitchElement,
} from "../Pitch";
import PitchElementData from "../PitchElementData";
import Shirt from "../Shirt";
import { UnstyledButton } from "../Utils";

const StyledSub = styled(Sub)`
  ${size(14)}

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    ${size(16)}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    ${size(20)}
  }
`;

const StyledButton = styled(UnstyledButton)`
  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    display: flex;
    width: 100%;
    padding: 0;
    border: 0;
    background-color: transparent;
    line-height: 1;
    cursor: pointer;
    height: auto;
    flex-direction: column;
    align-items: stretch;
    height: 100%;
    justify-content: start;
  }
`;

const StyledCaptain = styled(Captain)<IStyledCaptainProps>`
  ${size(14)};
  position: absolute;
  top: 22%;
  right: 4px;
  color: white;

  ${(props) =>
    props.variant === "2capt" &&
    css`
      color: black;
      fill: #fac101;
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    ${size(16)}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    ${size(20)}
  }
`;

const StyledViceCaptain = styled(ViceCaptain)<IStyledCaptainProps>`
  ${size(14)};
  position: absolute;
  top: 22%;
  right: 4px;
  color: white;

  ${(props) =>
    props.variant === "2capt" &&
    css`
      color: black;
      fill: #fac101;
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    ${size(16)}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    ${size(20)}
  }
`;

const StyledX2 = styled(x2)`
  ${size(14)};
  position: absolute;
  top: 22%;
  right: 4px;
  color: black;
  fill: #fac101;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    ${size(16)}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    ${size(20)}
  }
`;

const DreamTeamWrap = styled.div`
  position: absolute;
  top: 44%;
  right: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    right: 10px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    right: 20px;
  }
`;

interface IPropsFromState {
  elementsById: IElementsById;
  rules: IRules | null;
}

interface IPropsFromDispatch {
  showElementDialog: (elementId: number) => void;
}

interface IOwnProps {
  actionMe: () => void;
  chipName: string | null;
  pick: IPickLight;
  renderDreamTeam: (pick: IPickLight) => React.ReactNode;
  renderElementMenu: () => void;
  renderPickValue: (pick: IPickLight) => React.ReactNode;
}

interface IStyledCaptainProps {
  variant?: string;
}

type Props = IPropsFromState & IPropsFromDispatch & IOwnProps;

const TeamPitchElement: React.FC<Props> = ({
  actionMe,
  chipName,
  pick,
  elementsById,
  renderElementMenu,
  renderPickValue,
  renderDreamTeam,
  rules,
}) => {
  if (!rules) return null;

  const element = elementsById[pick.element];

  return (
    <PitchElementWrap variant={pick.subStatus || ""}>
      <StyledPitchElement>
        <StyledButton type="button" onClick={renderElementMenu}>
          <Shirt elementId={element.id} />
          <PitchElementData element={element} value={renderPickValue(pick)} />
        </StyledButton>
        <Controls>
          {actionMe && typeof pick.subStatus !== "undefined" && (
            <PrimaryControl>
              <UnstyledButton type="button" onClick={actionMe}>
                <StyledSub />
              </UnstyledButton>
            </PrimaryControl>
          )}
          <InfoControl>
            <ElementDialogButton elementId={pick.element} />
          </InfoControl>
        </Controls>

        {pick.is_captain &&
          (!chipName || chipName === "rich" || chipName === "wildcard") && (
            <StyledCaptain />
          )}

        {pick.is_vice_captain &&
          (!chipName || chipName === "rich" || chipName === "wildcard") && (
            <StyledViceCaptain />
          )}

        {pick.is_captain && chipName === "2capt" && (
          <StyledCaptain variant="2capt" />
        )}

        {pick.is_vice_captain && chipName === "2capt" && (
          <StyledViceCaptain variant="2capt" />
        )}

        {chipName === "frush" &&
          element.element_type === 4 &&
          pick.position <= rules.squad_squadplay && <StyledX2 />}

        {chipName === "pdbus" &&
          element.element_type === 2 &&
          pick.position <= rules.squad_squadplay && <StyledX2 />}

        <DreamTeamWrap>{renderDreamTeam(pick)}</DreamTeamWrap>
      </StyledPitchElement>
    </PitchElementWrap>
  );
};

const mapStateToProps = (state: RootState): IPropsFromState => ({
  elementsById: getElementsById(state),
  rules: getRules(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch): IPropsFromDispatch => ({
  showElementDialog: (elementId) => dispatch(showElementSummary(elementId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TeamPitchElement);
